import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingDown, LineChart, Activity } from 'lucide-react';
import { Button } from './ui/Button';
interface SlideLifecycleProps {
  onContinue: () => void;
}
const LEVERS = [{
  title: 'Reduce erosion',
  desc: 'Continuous, data-driven engagement keeps HCPs connected to the brand — actively defending share instead of letting it fade.',
  icon: TrendingDown,
  surface: 'bg-teal-400/[0.07] border-teal-400/30',
  chip: 'bg-teal-400/15 text-teal-400'
}, {
  title: 'Increase profitability',
  desc: 'Replacing traditional marketing with automated execution widens the margin on every euro of legacy revenue.',
  icon: LineChart,
  surface: 'bg-purple-500/[0.08] border-purple-500/30',
  chip: 'bg-purple-500/15 text-purple-400'
}, {
  title: 'Prolong lifespan',
  desc: 'Lower costs and slower erosion keep the gap between revenue and cost positive for longer, keeping products on market.',
  icon: Activity,
  surface: 'bg-rose-500/[0.07] border-rose-400/30',
  chip: 'bg-rose-500/15 text-rose-400'
}] as const;
export function SlideLifecycle({
  onContinue
}: SlideLifecycleProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 600); // axes
    const t2 = setTimeout(() => setStage(2), 1400); // main curve
    const t3 = setTimeout(() => setStage(3), 4000); // phase labels + bands
    const t4 = setTimeout(() => setStage(4), 5800); // potential area
    const t5 = setTimeout(() => setStage(5), 6600); // the three levers
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
    };
  }, []);
  const mainCurve = 'M 80 290 C 140 290 170 380 230 380 C 290 380 340 340 380 290 C 410 250 430 130 460 90 C 540 70 650 70 730 90 C 745 95 760 245 785 275 C 850 283 920 287 960 290';
  const potentialCurve = 'M 730 90 C 775 100 790 245 815 268 C 875 265 935 267 985 268';
  const potentialArea = 'M 730 90 C 775 100 790 245 815 268 C 875 265 935 267 985 268 L 960 290 C 920 287 850 283 785 275 C 760 245 745 95 730 90 Z';
  const phases = [{
    id: 'rd',
    label: 'R&D',
    sub: 'Investment & clinical trials',
    x: 230
  }, {
    id: 'launch',
    label: 'Launch',
    sub: 'Heavy marketing spend',
    x: 420
  }, {
    id: 'mature',
    label: 'Mature',
    sub: 'Peak revenue & share',
    x: 595
  }, {
    id: 'legacy',
    label: 'Legacy',
    sub: 'Loss of exclusivity',
    x: 845,
    accent: true
  }];
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto relative" data-id="element-831" data-mp="gs94dumss25y">
      {/* Snakker logo top-right */}
      <div className="absolute top-6 right-8 md:top-10 md:right-12 flex items-center gap-2 z-10" data-id="element-832" data-mp="3kbklzq8hc9il">
        <div className="w-8 h-8 rounded-lg bg-teal-400 flex items-center justify-center" data-id="element-833" data-mp="3p9m0dxuynknq">
          <div className="w-3.5 h-3.5 rounded-full bg-white" data-id="element-834" data-mp="26erazy8pw64p" />
        </div>
        <span className="text-teal-400 font-bold text-base tracking-tight" data-id="element-835" data-mp="26com5hjawj1k">
          snakker
        </span>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center space-y-4" data-id="element-836" data-mp="1xe2xd4n8k7sc">
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-837" data-mp="1ngot03el79ge">
          Unlocking the potential of the tail
        </h2>
      </motion.div>

      <div className="flex-1 min-h-0 relative flex items-center justify-center py-2" data-id="element-838" data-mp="1io5ff6fwgebl">
        <div className="w-full max-w-5xl max-h-full relative" style={{
        aspectRatio: '2.2 / 1'
      }} data-id="element-839" data-mp="1eomrs2wp1p52">
          <svg viewBox="0 0 1000 480" className="w-full h-full overflow-visible" data-id="element-840" data-mp="14pe4fswc38ro">
            <defs data-id="element-841" data-mp="zq2nojvyw3a3">
              <pattern id="hatch" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)" data-id="element-842" data-mp="zrvsruqxmffz">
                <line x1="0" y1="0" x2="0" y2="8" stroke="#00D4AA" strokeWidth="1.5" strokeOpacity="0.4" data-id="element-843" data-mp="34rdo1rubb24e" />
              </pattern>
            </defs>

            {/* + and − axis labels */}
            <motion.text x="60" y="100" textAnchor="middle" className="fill-white/35 text-xs font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-844" data-mp="2h4ozwoepmdfc">
              +
            </motion.text>
            <motion.text x="60" y="385" textAnchor="middle" className="fill-white/35 text-xs font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-845" data-mp="5dl2g37qs0lu">
              −
            </motion.text>

            <motion.text x="30" y="240" textAnchor="middle" className="fill-white/40 text-xs font-medium" style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed'
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-846" data-mp="24krzwalvzzwl">
              Cumulative Value
            </motion.text>
            <motion.text x="520" y="455" textAnchor="middle" className="fill-white/40 text-xs font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} data-id="element-847" data-mp="2ik15kgcgvbmb">
              Time
            </motion.text>

            {/* Y axis */}
            <motion.line x1="80" y1="40" x2="80" y2="430" stroke="#3A5278" strokeWidth="2" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.6
          }} data-id="element-848" data-mp="2t7301s5tlcd9" />

            {/* Zero line */}
            <motion.line x1="80" y1="290" x2="970" y2="290" stroke="#3A5278" strokeWidth="2" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.8
          }} data-id="element-849" data-mp="3gw5cmlllkf0w" />
            <motion.text x="975" y="295" className="fill-white/30 text-[10px] font-medium" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} transition={{
            delay: 0.4
          }} data-id="element-850" data-mp="8waeosvocvor">
              €0
            </motion.text>

            {/* Phase dividers */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 0.5 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-851" data-mp="mllzvti6tzgj">
              <line x1="380" y1="50" x2="380" y2="430" stroke="#3A5278" strokeWidth="1" strokeDasharray="4,4" data-id="element-852" data-mp="drzhr66wkupt" />
              <line x1="460" y1="50" x2="460" y2="430" stroke="#3A5278" strokeWidth="1" strokeDasharray="4,4" data-id="element-853" data-mp="1p8qqbr0lk27w" />
              <line x1="730" y1="50" x2="730" y2="430" stroke="#3A5278" strokeWidth="1" strokeDasharray="4,4" data-id="element-854" data-mp="1fcnrov4yelpp" />
            </motion.g>

            {/* Highlight band on Legacy phase */}
            <motion.rect x="730" y="50" width="230" height="380" fill="#00D4AA" fillOpacity="0.04" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 0.6
          }} data-id="element-855" data-mp="2cwidjpnodzny" />

            {/* Potential hatched area */}
            <motion.path d={potentialArea} fill="url(#hatch)" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.0
          }} data-id="element-856" data-mp="35rg7pil8q9tl" />
            <motion.path d={potentialArea} fill="rgba(0, 212, 170, 0.08)" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.0
          }} data-id="element-857" data-mp="3aq9jn6kvpsrn" />

            {/* Main curve */}
            <motion.path d={mainCurve} fill="none" stroke="#0f172a" strokeWidth="3" strokeOpacity="0.95" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 2 ? 1 : 0
          }} transition={{
            duration: 2.6,
            ease: 'easeInOut'
          }} data-id="element-858" data-mp="2r20ja996mgh2" />

            {/* Potential dashed extension */}
            <motion.path d={potentialCurve} fill="none" stroke="#00D4AA" strokeWidth="3" strokeDasharray="8,4" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.4,
            ease: 'easeInOut'
          }} data-id="element-859" data-mp="1mewc118kjv3s" />

            {/* Phase labels */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-860" data-mp="ylnydfv8i5e7">
              {phases.map(p => <g key={p.label} data-id="element-861" data-mp="35br888zgpjb7">
                  <text x={p.x} y="450" textAnchor="middle" className={`text-sm font-semibold ${p.accent ? 'fill-teal-400' : 'fill-white/65'}`} data-id="element-862" data-mp="35aok28tixuyz">
                    {p.label}
                  </text>
                  <text x={p.x} y="468" textAnchor="middle" className={`text-xs ${p.accent ? 'fill-teal-300/60' : 'fill-white/35'}`} data-id="element-863" data-mp="2hn81hxw29el8">
                    {p.sub}
                  </text>
                </g>)}
            </motion.g>

            {/* POTENTIAL label */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 0.6,
            delay: 0.6
          }} data-id="element-864" data-mp="1yw759hmbrx5d">
              <line x1="845" y1="215" x2="900" y2="178" stroke="#00D4AA" strokeWidth="1" strokeOpacity="0.6" data-id="element-865" data-mp="319lhi3qd61ha" />
              <text x="908" y="170" className="fill-teal-400 text-sm font-bold tracking-wider" data-id="element-866" data-mp="3b07tzp2sabvr">
                POTENTIAL
              </text>
              <text x="908" y="186" className="fill-teal-300/60 text-[10px]" data-id="element-867" data-mp="2ibsfr4rh7xlh">
                Softening the cliff
              </text>
            </motion.g>

            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 0.7 : 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-868" data-mp="2s7vpavonqyig">
              <text x="230" y="410" textAnchor="middle" className="fill-white/40 text-[10px] italic" data-id="element-869" data-mp="f15u8pfbv69n">
                Investment
              </text>
            </motion.g>

            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 0.7 : 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-870" data-mp="55cd0sqnyolj">
              <text x="595" y="55" textAnchor="middle" className="fill-white/40 text-[10px] italic" data-id="element-871" data-mp="12qi5duu7toel">
                Peak Revenue
              </text>
            </motion.g>
          </svg>

          {/* Floating insight card */}
        </div>
      </div>

      <div className="absolute -left-20 top-0 bottom-0 flex flex-col justify-between text-xs text-white/40 font-medium py-4" data-id="element-872" data-mp="1lhhls7nn9pw9">
        <span data-id="element-873" data-mp="3tzvdv2i9v9ya">Revenue</span>
        <span data-id="element-874" data-mp="3u055svmvnr6m">Investment</span>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 5 ? 1 : 0,
      y: stage >= 5 ? 0 : 20
    }} className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto w-full shrink-0 mt-4" data-id="element-875" data-mp="3p00lsvmrgebn">
        {LEVERS.map(lever => {
        const Icon = lever.icon;
        return <div key={lever.title} className={`rounded-2xl border p-4 ${lever.surface}`} data-id="element-876" data-mp="bkul63sy09vp">
              <div className="flex items-center gap-2.5 mb-2" data-id="element-877" data-mp="6tchv93g5x7k">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${lever.chip}`} data-id="element-878" data-mp="6rrrzws2v2g1">
                  <Icon className="w-4 h-4" data-id="element-879" data-mp="zbgqhnbkbg20" />
                </div>
                <h3 className="text-white font-semibold text-sm" data-id="element-880" data-mp="ujypx0dgbhhk">
                  {lever.title}
                </h3>
              </div>
              <p className="text-white/65 text-[13px] leading-snug" data-id="element-881" data-mp="12tnzuxibuyo1">
                {lever.desc}
              </p>
            </div>;
      })}
      </motion.div>

    </div>;
}